import { useEffect, useRef, useState } from 'react'
import { saveAs } from '@/lib/download'
import { toRelUrl } from '@/lib/mediaUrl'
import { Icon } from './icons'
import { Button } from './ui'

/** 秒 → `m:ss`。`NaN` / 负值一律按 0 显示 —— 元数据没到时别印 `NaN:NaN`。 */
function fmtClock(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '0:00'
  const s = Math.floor(sec)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/**
 * 浏览器原话 → 给用户看的一句话。
 *
 * 两件事，都不是美化：
 *  ① **去掉尾巴上的网址**。Chrome 会给 `play()` 失败的原话缀一个
 *     `https://goo.gl/LdtK22`（它的「为什么会这样」帮助页）。那串东西占掉
 *     半条播放器的宽度，把真正的信息挤成省略号 —— 用户看到的是一行英文链接。
 *  ② **`AbortError` 要认出来**。它是「上一次 `play()` 还没 resolve，就被
 *     `pause()` 打断了」，浏览器原话是
 *     `The play() request was interrupted by a call to pause().`。
 *     这不是故障，是**换曲的正常因果链**（停旧的 → 播新的）。
 *     2026-10-09 用户截图里那行红字就是它 —— 报了一个从未发生的错误。
 *     判别用 `name`（`AbortError`）而不是比 message：原话各浏览器措辞不一。
 */
function playFailureText(e: unknown): string | null {
  if (e instanceof DOMException && e.name === 'AbortError') return null
  const raw = e instanceof Error ? e.message : String(e)
  return raw.replace(/\s*https?:\/\/\S+\s*$/, '').trim()
}

/**
 * 底部试听条（`/tts` 专用）。
 *
 * ## 它是「一条常驻的播放器」，不是「又一份音频播放实现」
 *
 * 播放、进度、音量全部交给**真实的 `<audio>` 元素**（藏在下面，只负责出声），
 * 这里画的是它的**仪表盘**。这条界线必须守住：
 * 自研播放器最典型的故障是「按钮变成暂停图标、其实一点声都没有」——
 * 界面的播放态一旦由本地 `useState` 乐观决定，就再也没人能从屏幕上发现它错了。
 * 所以状态**只从 `onPlay` / `onPause` / `onEnded` / `onError` 这些真事件回流**，
 * `playing` 这个 state 没有任何一处由点击直接写。
 *
 * `ResultGrid` 里那张结果卡仍然保留原生 `<audio controls>`：那里是「逐条核对产出」，
 * 原生控件更直接。两者**不是两个入口改同一个值** —— 它们改的是同一份产出、
 * 不同时刻的播放实例，互不冲突（这也是本项目唯一允许的音频控件重复）。
 *
 * ## ⚠️ `track` 必须由调用方给全，不许在这里回落到「最新一条」
 *
 * 这一条是 2026-10-09 报障（「历史里的播放键点了没反应 / 播的不是那条」）。
 * 试听条自己无从判断「用户点的是哪一条还在不在」—— 那是**调用方的知识**：
 * `/tts` 的历史记录产出**不属于本轮产出**（`outputsFor` 刻意排除远端记录），
 * 谁在这里「找不到就退回第一条」，结果就是**点了 A 响 B**，
 * 而条上的标题还写着 A。所以本组件只认传进来的那一条地址，找不到就不该被渲染。
 *
 * ## 进度条用 `<input type="range">` 而不是自绘 div
 *
 * 键盘可拖、无障碍语义免费（`role=slider` 那套不用自己补）。已播部分靠 `--p`
 * 变量画成一段渐变，省掉一层填充元素。
 */
export function AudioPlayerBar({
  track,
  title,
  subtitle,
  playToken = 0,
}: {
  /**
   * 当前要播的这条产出。`null` 时调用方就不渲染本组件（空条是噪音）。
   *
   * ⚠️ 类型刻意只要 `url`（不是整个 `JobOutput`）：本组件**只认地址**，
   * 标题与副标题走各自的 prop。收窄成 `{url}` 之后，两个调用方都能直接传 ——
   * `/tts` 传的是「用户点过的那条」的描述（可能来自历史记录，
   * 不在本轮产出里，也就没有完整 `JobOutput` 对象），数字人页传的是配音产出。
   * 要求完整 `JobOutput` 会逼调用方去拼一个假对象，那是本末倒置。
   */
  track: { url: string }
  /** 展示名。产出本身没有标题，由调用方按「第 N 条 / 任务标题」给一个 */
  title: string
  /** 副标题，通常是模型名或引擎 */
  subtitle?: string
  /**
   * 「用户刚刚点了某条产出的播放键」的信号 —— 每次点击递增。
   *
   * 为什么需要一个计数器，而不是「换了 track 就自动播」：本组件的出现有**两种来路**，
   * 而它们要求相反的行为 ——
   *   · 新产出落盘、试听条自己冒出来 → **不要**自动出声（用户没点任何东西）；
   *   · 用户点了结果卡上的「在底部播放」→ **要**出声（他点的就是播放）。
   * 只看 `track` 变没变，这两种来路长得一模一样。于是由调用方在**真的有人点**时
   * 递增这个数，播放行为就和用户的意图同源了。
   *
   * 默认 0 = 调用方从不递增 = 永不自动播，按钮永远说了算。
   */
  playToken?: number
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [cur, setCur] = useState(0)
  const [dur, setDur] = useState(0)
  const [err, setErr] = useState<string | null>(null)

  const src = toRelUrl(track.url)

  /**
   * 播 —— **只把真故障报出来**。
   *
   * ⚠️ 「换曲」那一段会 `pause()`，而 `pause()` 会把上一次还没 resolve 的
   * `play()` 打断成 `AbortError`。那是正常的（停旧的、播新的），
   * 不是这次播放失败 —— 详见 `playFailureText`。
   *
   * 真故障（编码不支持 / 自动播放被拦 / 地址失效）照旧**必须说出来**：
   * 静默失败的样子是「按钮变成了暂停图标、一点声都没有」，
   * 而此前那条注释写的就是「不许静默什么都不发生」。
   */
  const play = () => {
    const el = audioRef.current
    if (!el) return
    setErr(null)
    void el.play().catch((e: unknown) => {
      const msg = playFailureText(e)
      if (msg) setErr(`播放失败：${msg}`)
    })
  }

  /**
   * 换曲：把播放器**退回起点并停住**。
   *
   * 刻意不自动播 —— 自动播在「刚生成完、这条自己冒出来」的时刻是噪音，
   * 而在「用户点了某条结果的播放键」的时刻又是他正想要的。同一个组件分不清两者，
   * 那就别猜：交给那颗 40px 的播放键。`error` 也在这里清，否则上一条的报错
   * 会挂在下一条身上（假故障比真故障更难查）。
   */
  useEffect(() => {
    const el = audioRef.current
    // 不用 `el` 早退 —— 首次渲染时 ref 还没挂上，早退会把状态复位也一起吞掉
    setPlaying(false)
    setCur(0)
    setDur(0)
    setErr(null)
    if (el) {
      el.pause()
      el.currentTime = 0
    }
  }, [src])

  /**
   * 「用户点了播放」→ 真的去播。
   *
   * ⚠️ 这个 effect **必须排在换曲 effect 之后**：`src` 与 `playToken` 常常同一批更新
   * （点另一条产出 = 换曲 + 放行播放），React 按声明顺序跑，先重置再播放才对。
   * 顺序反了的话表现为「第一次点能播、换一条再点就没反应」。
   */
  useEffect(() => {
    if (!playToken) return
    play()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playToken])

  const toggle = () => {
    const el = audioRef.current
    if (!el) return
    if (el.paused) {
      play()
    } else {
      el.pause()
    }
  }

  const onSeek = (v: number) => {
    const el = audioRef.current
    if (!el || !dur) return
    const t = (v / 100) * dur
    el.currentTime = t
    setCur(t)
  }

  const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0
  const fileName = `语音合成-${title.replace(/[\\/:*?"<>|]/g, '_')}.mp3`

  return (
    <div className="tts-player" data-player="tts">
      <div className="tts-player__art" aria-hidden="true">
        <Icon name="wave" size={20} />
      </div>

      <div className="tts-player__meta">
        <div className="tts-player__title" title={title}>
          {title}
        </div>
        <div className="tts-player__sub">{subtitle ?? '语音合成'}</div>
      </div>

      <button
        type="button"
        className="tts-player__play"
        onClick={toggle}
        aria-label={playing ? '暂停' : '播放'}
        data-playing={playing}
      >
        <Icon name={playing ? 'pause' : 'play'} size={18} />
      </button>

      {err ? (
        /*
          报错**只占一行、不许撑开整条**（2026-10-09 用户口径：「报错文字还会撑开，
          把左侧挤变形」）。原来它 `flex: 1 1 auto` 顶掉进度条与时间，
          原话长的（带网址那种）就整条换行。现在：单行省略 + `title` 兜全文，
          进度条与时间**照常在位** —— 报错不该把播放器的仪表盘拆了。
        */
        <span className="tts-player__err" title={err} data-player-err="1">
          {err}
        </span>
      ) : null}

      <>
        <input
          type="range"
          className="tts-player__seek"
          min={0}
          max={100}
          step={0.1}
          value={pct}
          style={{ ['--p' as string]: `${pct}%` }}
          onChange={(e) => onSeek(Number(e.target.value))}
          aria-label="播放进度"
        />
        <span className="tts-player__time">
          {fmtClock(cur)} / {fmtClock(dur)}
        </span>
      </>

      <Button
        size="icon"
        variant="ghost"
        icon="download"
        title="下载这条音频"
        aria-label="下载这条音频"
        onClick={() => void saveAs(track.url, fileName, 'mp3')}
      />

      {/*
        真正的播放引擎。`display:none` 不影响出声（浏览器不会因为不可见就停播），
        所以要「不出声的试听条」是不存在的 —— 用户听到的就是这一段。
      */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false)
          setCur(dur)
        }}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onDurationChange={(e) => setDur(e.currentTarget.duration)}
        onError={() => setErr('这条音频加载失败 —— 产出可能已被清理，或地址已过期。')}
        style={{ display: 'none' }}
      />
    </div>
  )
}
